import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

const ORGANIZATION_SIZE_OPTIONS = [
  { id: 'SMALL', name: 'Small (1-50 employees)', description: 'Startups and small businesses' },
  { id: 'MEDIUM', name: 'Medium (51-250 employees)', description: 'Growing companies' },
  { id: 'LARGE', name: 'Large (251-1000 employees)', description: 'Established enterprises' },
  { id: 'ENTERPRISE', name: 'Enterprise (1000+ employees)', description: 'Large corporations' },
];

const GEOGRAPHIC_SCOPE_OPTIONS = [
  { id: 'LOCAL', name: 'Local', description: 'Single city or region' },
  { id: 'REGIONAL', name: 'Regional', description: 'Multiple cities or states' },
  { id: 'NATIONAL', name: 'National', description: 'Country-wide operations' },
  { id: 'GLOBAL', name: 'Global', description: 'International operations' },
];

const TIMEZONE_OPTIONS = [
  'UTC-12:00', 'UTC-11:00', 'UTC-10:00', 'UTC-09:00', 'UTC-08:00', 'UTC-07:00',
  'UTC-06:00', 'UTC-05:00', 'UTC-04:00', 'UTC-03:00', 'UTC-02:00', 'UTC-01:00',
  'UTC+00:00', 'UTC+01:00', 'UTC+02:00', 'UTC+03:00', 'UTC+04:00', 'UTC+05:00',
  'UTC+06:00', 'UTC+07:00', 'UTC+08:00', 'UTC+09:00', 'UTC+10:00', 'UTC+11:00', 'UTC+12:00'
];

const LANGUAGE_OPTIONS = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'de-DE', name: 'Deutsch (German)' },
  { code: 'fr-FR', name: 'Français (French)' },
  { code: 'es-ES', name: 'Español (Spanish)' },
  { code: 'it-IT', name: 'Italiano (Italian)' },
  { code: 'pt-BR', name: 'Português (Portuguese)' },
  { code: 'ja-JP', name: '日本語 (Japanese)' },
  { code: 'ko-KR', name: '한국어 (Korean)' },
  { code: 'hi-IN', name: 'हिन्दी (Hindi)' },
  { code: 'mr-IN', name: 'मराठी (Marathi)' },
  { code: 'zh-CN', name: '中文 (Chinese)' },
  { code: 'ar-SA', name: 'العربية (Arabic)' },
];

export const OrganizationDetailsStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    organizationSize: data.organizationSize || '',
    geographicScope: data.geographicScope || '',
    companyName: data.companyName || '',
    domain: data.domain || '',
    primaryLocation: data.primaryLocation || '',
    timeZone: data.timeZone || 'UTC+00:00',
    language: data.language || 'en-US',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    onNext({
      organizationSize: formData.organizationSize,
      geographicScope: formData.geographicScope,
      companyName: formData.companyName,
      domain: formData.domain,
      primaryLocation: formData.primaryLocation,
      timeZone: formData.timeZone,
      language: formData.language,
    });
  };

  const canGoNext = !!(formData.organizationSize && formData.geographicScope && 
                   formData.companyName && formData.domain && 
                   formData.primaryLocation);

  return (
    <OnboardingStep
      title={t('onboarding.organization.title')}
      description={t('onboarding.organization.description')}
      onNext={handleNext}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={true}
    >
      <div className="space-y-8">
        {/* Organization Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('onboarding.organization.size')}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ORGANIZATION_SIZE_OPTIONS.map((option) => (
              <label
                key={option.id}
                className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 ${
                  formData.organizationSize === option.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="organizationSize"
                  value={option.id}
                  checked={formData.organizationSize === option.id}
                  onChange={(e) => handleInputChange('organizationSize', e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {option.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {option.description}
                    </div>
                  </div>
                  {formData.organizationSize === option.id && (
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Geographic Scope */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('onboarding.organization.geographicScope')}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {GEOGRAPHIC_SCOPE_OPTIONS.map((option) => (
              <label
                key={option.id}
                className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 ${
                  formData.geographicScope === option.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="geographicScope"
                  value={option.id}
                  checked={formData.geographicScope === option.id}
                  onChange={(e) => handleInputChange('geographicScope', e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {option.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {option.description}
                    </div>
                  </div>
                  {formData.geographicScope === option.id && (
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Company Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.organization.companyName')} *
            </label>
            <input
              type="text"
              id="companyName"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('onboarding.organization.companyNamePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.organization.domain')} *
            </label>
            <input
              type="text"
              id="domain"
              value={formData.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="example.com"
            />
          </div>

          <div>
            <label htmlFor="primaryLocation" className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.organization.primaryLocation')} *
            </label>
            <input
              type="text"
              id="primaryLocation"
              value={formData.primaryLocation}
              onChange={(e) => handleInputChange('primaryLocation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('onboarding.organization.primaryLocationPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.organization.timeZone')}
            </label>
            <select
              id="timeZone"
              value={formData.timeZone}
              onChange={(e) => handleInputChange('timeZone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.organization.language')}
            </label>
            <select
              id="language"
              value={formData.language}
              onChange={(e) => handleInputChange('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </OnboardingStep>
  );
};
